import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { supabase } from '../../lib/supabase';
import NoiRockLogo from '../../components/NoiRockLogo';
import LangSwitcher from '../../components/LangSwitcher';
import ConnectionBadge from './components/ConnectionBadge';
import LoginForm from './components/LoginForm';
import TcnLoginDesktop from './TcnLoginDesktop';
import { getHostTenantSlug, getHostTenant } from '../../lib/hostTenant';

const ALL_LANGS = ['fr', 'en', 'it', 'de', 'ar'];

// Map a (role, tenantSlug) pair to its default landing route.
// TCN tenant has its own dark-themed app with separate routes.
const routeForRoleAndTenant = (role, tenantSlug) => {
  if (role === 'super_admin') return '/superadmin';

  // TCN tenant — dedicated routes
  if (tenantSlug === 'tcn') {
    switch (role) {
      // tenant_admin = TCN HQ admin who configures objective rules + business rules
      // (does NOT inject Excel files — that's the agency_owner's job)
      case 'tenant_admin':  return '/admin/objective-rules';
      case 'agency_owner':  return '/tcn/admin/upload';
      case 'manager':
      case 'agent':         return '/tcn/me';
      case 'creator':       return '/tcn/my/dashboard';
      default:              return '/tcn/admin/upload';
    }
  }

  // Generic / default tenants — existing NoiRock routes
  switch (role) {
    case 'tenant_admin':  return '/admin/dashboard';
    case 'agency_owner':  return '/admin/dashboard';
    case 'manager':
    case 'agent':         return '/manager/dashboard';
    case 'creator':       return '/my/dashboard';
    default:              return '/admin/dashboard';
  }
};

const LoginAuthentication = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [connectionStatus, setConnectionStatus] = useState('checking');
  // Brand + allowed languages. On the apex (no subdomain tenant) we keep NoiRock
  // + all locales. On a dedicated subdomain (tcn.noirock.app) we resolve them
  // from the DB (tenants.enabled_languages) so the picker reflects super_admin
  // config — add a language there and it shows up here automatically.
  const [brand, setBrand] = useState({ name: 'NoiRock', languages: ALL_LANGS });

  useEffect(() => {
    const slug = getHostTenantSlug();
    if (!slug) return undefined; // apex: defaults
    let cancelled = false;
    (async () => {
      const fb = getHostTenant();
      let languages = fb?.languages ?? null;
      let name = fb?.name ?? null;
      // Source of truth: DB (anon-safe RPC, branding fields only).
      const { data, error } = await supabase.rpc('tenant_public_branding', { p_slug: slug });
      if (!cancelled && !error && Array.isArray(data) && data[0]) {
        if (Array.isArray(data[0].enabled_languages) && data[0].enabled_languages.length) {
          languages = data[0].enabled_languages;
        }
        if (data[0].name) name = data[0].name;
      }
      if (cancelled || !languages?.length) return;
      setBrand({ name: name || 'NoiRock', languages });
      const cur = (i18n.language || 'en').split('-')[0];
      if (!languages.includes(cur)) {
        const def = languages.includes('en') ? 'en' : languages[0];
        i18n.changeLanguage(def);
        document.documentElement.dir = def === 'ar' ? 'rtl' : 'ltr';
        document.documentElement.lang = def;
      }
    })();
    return () => { cancelled = true; };
  }, [i18n]);

  // Lightweight liveness check — pings tenants. RLS may return [], that's fine
  // (we only care that the request completes without a network/auth error).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { error } = await supabase.from('tenants').select('id').limit(1);
        if (!cancelled) setConnectionStatus(error ? 'error' : 'connected');
      } catch {
        if (!cancelled) setConnectionStatus('error');
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Resolve role + tenantSlug for a given user id (2 queries — robust to PostgREST embed quirks)
  const resolveRouteContext = async (userId) => {
    const { data: profile } = await supabase
      .from('profiles')
      .select('role, tenant_id')
      .eq('id', userId)
      .single();
    if (!profile) return { role: null, tenantSlug: null };
    let tenantSlug = null;
    if (profile.tenant_id) {
      const { data: tenant } = await supabase
        .from('tenants')
        .select('slug')
        .eq('id', profile.tenant_id)
        .single();
      tenantSlug = tenant?.slug || null;
    }
    return { role: profile.role, tenantSlug };
  };

  // If already signed in, redirect according to (role, tenantSlug)
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session || cancelled) return;
      const ctx = await resolveRouteContext(session.user.id);
      if (cancelled) return;
      navigate(routeForRoleAndTenant(ctx.role, ctx.tenantSlug), { replace: true });
    })();
    return () => { cancelled = true; };
  }, [navigate]);

  // Called by LoginForm after a successful auth+profile fetch.
  // The profile may not include `id` (depends on the form's select) — we re-fetch
  // the session to get user id reliably.
  const handleLoginSuccess = async (profile) => {
    const { data: { session } } = await supabase.auth.getSession();
    const userId = session?.user?.id;
    if (!userId) return;
    // Fast path: tenant_id already on profile from form
    let tenantSlug = null;
    if (profile?.tenant_id) {
      const { data: tenant } = await supabase
        .from('tenants')
        .select('slug')
        .eq('id', profile.tenant_id)
        .single();
      tenantSlug = tenant?.slug || null;
    }
    navigate(routeForRoleAndTenant(profile?.role, tenantSlug), { replace: true });
  };

  // TCN context (tcn.noirock.app) → dedicated split-screen design (real auth).
  // Apex / Generic keep the standard login below.
  if (getHostTenantSlug() === 'tcn') {
    return (
      <TcnLoginDesktop
        brandName={brand.name}
        languages={brand.languages}
        onSuccess={handleLoginSuccess}
      />
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-background relative overflow-hidden">
      {/* Soft ambient brand glow (light-theme friendly) */}
      <div className="absolute inset-0 pointer-events-none">
        <div
          className="absolute -top-32 left-1/2 -translate-x-1/2 w-[680px] h-[420px] rounded-full opacity-40"
          style={{
            background: 'radial-gradient(ellipse, rgba(67, 97, 238, 0.18) 0%, transparent 70%)',
            filter: 'blur(60px)',
          }}
        />
        <div
          className="absolute bottom-0 right-0 w-[420px] h-[320px] rounded-full opacity-30"
          style={{
            background: 'radial-gradient(ellipse, rgba(124, 58, 237, 0.15) 0%, transparent 70%)',
            filter: 'blur(80px)',
          }}
        />
      </div>

      {/* Top bar */}
      <header className="relative z-10 flex items-center justify-between px-6 py-5">
        <NoiRockLogo size={28} withText />
        <div className="flex items-center gap-3">
          <LangSwitcher langs={brand.languages} />
          <ConnectionBadge status={connectionStatus} />
        </div>
      </header>

      {/* Centered card */}
      <main className="relative z-10 flex-1 flex items-center justify-center px-4 py-12">
        <div className="w-full max-w-sm">
          <div className="mb-8 text-center">
            <div className="inline-flex flex-col items-center gap-3 mb-4">
              <NoiRockLogo size={56} />
              <h1 className="font-heading font-bold tracking-tight text-2xl text-foreground">
                {brand.name}
              </h1>
            </div>
            <p className="text-muted-foreground text-sm leading-relaxed">
              {t('login.tagline', 'Multi-tenant SaaS for TikTok LIVE agencies')}
            </p>
          </div>

          <div className="rounded-2xl bg-card border border-border shadow-elevation-2 p-7">
            <div className="mb-6">
              <h2 className="font-heading text-lg font-semibold text-card-foreground mb-1">
                {t('login.welcomeBack', 'Welcome back')}
              </h2>
              <p className="text-muted-foreground text-sm">
                {t('login.signInSubtitle', 'Sign in to your NoiRock account')}
              </p>
            </div>

            <LoginForm onSuccess={handleLoginSuccess} />
          </div>

          <div className="mt-6 flex items-center justify-center gap-4 text-xs text-muted-foreground">
            <Link to="/terms" className="hover:text-foreground transition-colors">
              {t('login.terms', 'Terms')}
            </Link>
            <span>·</span>
            <Link to="/privacy" className="hover:text-foreground transition-colors">
              {t('login.privacy', 'Privacy')}
            </Link>
          </div>
          <p className="text-center text-muted-foreground/70 text-xs mt-3">
            © 2026 NoiRock
          </p>
        </div>
      </main>
    </div>
  );
};

export default LoginAuthentication;
